import React, { useState, useRef, useEffect } from "react";
import { Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import Question from "./Question";
import Display from "./Display";

const Calculator = (props) => {
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [missingInfoError, setMissingInfoError] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firstCalc, setFirstCalc] = useState(true);
  const resultRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [inputHeight, setInputHeight] = useState(0);
  const isMobile = useMediaQuery('(max-width:600px)');

  const isVisible = (q) => {
    if (!q.condition) return true;
    const target = answers[q.condition.on];
    if (target === "" || target === null) return true;
    if (q.condition.operator === "lte") {
      return Number(target) <= q.condition.value;
    }
    return target === q.condition.value;
  };

  useEffect(() => {
    if (props.project) {
      import(`../configs/${props.project}.json`)
        .then(mod => {
          const loadedConfig = mod.default || mod;
          setConfig(loadedConfig);
          const initial = {};
          loadedConfig.questions.forEach(q => {
            if (q.field_type === "group" || q.field_type === "collapsible_group") {
              q.fields.forEach(subq => {
                initial[subq.id] = subq.default !== undefined ? subq.default : "";
              });
            } else if (q.field_type === "conditional_group") {
              // Initialize fixed field default for conditional group
              if (q.fixed_field) {
                initial[q.fixed_field.id] = q.fixed_field.default !== undefined ? q.fixed_field.default : "";
              }
            } else {
              initial[q.id] = q.default !== undefined ? q.default : "";
            }
          });
          setAnswers(initial);
        })
        .catch(error => {
          console.error("Failed to load config:", error);
        });
    }
  }, [props.project]);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [result]);

  useEffect(() => {
    if (inputContainerRef.current) {
      setInputHeight(inputContainerRef.current.offsetHeight);
    }
  }, [config, isMobile]);

  useEffect(() => {
    const updateInputHeight = () => {
      if (inputContainerRef.current) {
        setInputHeight(inputContainerRef.current.offsetHeight);
      }
    };
    window.addEventListener("resize", updateInputHeight);
    return () => window.removeEventListener("resize", updateInputHeight);
  }, []);

  const handleChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    if (value !== "" && value !== null) {
      setErrors(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleCalculate = async () => {
    if (!config) return;
    let currentErrors = {};
    config.questions.forEach(q => {
      if (q.field_type === "group") {
        if (q.condition && !isVisible(q)) return;
        q.fields.forEach(subq => {
          if (subq.required && isVisible(subq) && (answers[subq.id] === "" || answers[subq.id] === null)) {
            currentErrors[subq.id] = true;
          }
        });
      } else {
        if (q.required && isVisible(q) && (answers[q.id] === "" || answers[q.id] === null)) {
          currentErrors[q.id] = true;
        }
      }
    });
    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      setMissingInfoError(true);
      return;
    }
    setMissingInfoError(false);
    let startTime;
    if (firstCalc) {
      startTime = Date.now();
      setLoading(true);
    }
    try {
      const response = await fetch(config.calculation_endpoint, {
        method: "POST",
        body: JSON.stringify(answers)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const resultData = await response.json();
      if (firstCalc) {
        const elapsed = Date.now() - startTime;
        if (elapsed < 1000) {
          await new Promise(resolve => setTimeout(resolve, 1000 - elapsed));
        }
        setLoading(false);
        setFirstCalc(false);
      }
      setResult(resultData);
      console.log("Calculation result:", resultData);
    } catch (error) {
      console.error("Error during calculation:", error);
    }
  };

  if (!config) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  const groupQuestions = (questions) => {
    if (isMobile) {
      return questions.map(q => [{ question: q, span: 12 }]);
    }
    const rows = [];
    let currentRow = [];
    let currentSpan = 0;
    questions.forEach(q => {
      const span = q.layout && q.layout.columns ? 12 / q.layout.columns : 12;
      if (currentSpan + span > 12) {
        rows.push(currentRow);
        currentRow = [];
        currentSpan = 0;
      }
      currentRow.push({ question: q, span });
      currentSpan += span;
    });
    if (currentRow.length) rows.push(currentRow);
    return rows;
  };

  const groupedQuestions = groupQuestions(config.questions);

  return (
    <Grid container spacing={10} alignItems="flex-start">
      {/* Input/Calculate area */}
      <Grid item size={isMobile ? 12 : 5} ref={inputContainerRef} style={{ border: "4px solid #015f63", borderRadius: "15px", padding: "32px"}}>
        <Grid container spacing={3} direction="column">
          {config.title && (
            <Grid item xs={12} pb={2}>
              <Typography variant="h6" align="left" style={{ fontSize: '22px' }}>
                {config.title}
              </Typography>
            </Grid>
          )}
          {groupedQuestions.map((row, rowIndex) => {
            const visibleQuestions = row.filter(({ question }) => isVisible(question));
            if (!visibleQuestions.length) return null;
            return (
              <Grid container item spacing={4} key={rowIndex}>
                {visibleQuestions.map(({ question, span }) => (
                  <Grid item size={span} key={question.id}>
                    <Question
                      question={question}
                      tooltip={question.tooltip}
                      answers={answers}
                      errors={errors}
                      isVisible={isVisible}
                      handleChange={handleChange}
                    />
                  </Grid>
                ))}
              </Grid>
            );
          })}
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleCalculate}>
              Calculate
            </Button>
          </Grid>
          {missingInfoError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                Some necessary information is missing.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      {/* Result/Output area */}
      <Grid item size={isMobile ? 12 : 7} ref={resultRef} pt={0}>
        {loading ? (
          <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
            <CircularProgress />
          </Grid>
        ) : result ? (
          <Display result={result} config={config} />
        ) : (
          <Grid container direction="column" sx={{ 
              height: "100%", 
              justifyContent: "center", 
              alignItems: "center", 
              backgroundColor: "#f5f5f5", 
              padding: "20px", 
              borderRadius: "15px",
              minHeight: inputHeight || "auto"
            }}>
            <Typography variant="h6" align="center" style={{ fontSize: '22px' }}>
              Results Will Appear Here
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default Calculator;